import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { H3, Icon, NewButton, Input, InputBox, H4, Tooltip } from '../../../../../components';
import { useTermTranslations } from '../../../../chats/providers/TermTranslationsProvider';
import { BusinessLogicLoader } from '../../molecules/BusinessLogicLoader/BusinessLogicLoader';

const FormContainer = styled.div`
  position: fixed;
  top: 77px;
  right: 0;
  height: calc(100vh - 77px);
  width: 25%;
  background: var(--elevation-high);
  border-left: 1px solid var(--border-muted);
  display: flex;
  flex-direction: column;
  z-index: var(--z-modal);
  transform: translateX(${(props) => (props.isOpen ? '0' : '100%')});
  transition: transform 0.3s ease-in-out;
`;

const FormHeader = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-muted);
  flex-shrink: 0;
`;

const FormBody = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormFooter = styled.div`
  padding: 20px;
  border-top: 1px solid var(--border-muted);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: auto;
`;

const StyledForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StyledInputBox = styled(InputBox)`
  width: 100%;
  height: auto;
  min-height: 80px;
  padding: 12px;
  align-items: flex-start;

  textarea {
    resize: none;
  }
`;

const DefinitionLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EditTranslateContainer = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AddNewBusinessLogicForm = ({ isOpen, onClose }) => {
  const [definition, setDefinition] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [originalDefinition, setOriginalDefinition] = useState('');
  const {
    createTermTranslation,
    createTranslation,
    isTranslating,
    translatedTerm,
    clearTranslatedTerm
  } = useTermTranslations();

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!definition.trim()) {
      return;
    }

    await createTermTranslation(definition);

    // Close the form after successful submission
    setDefinition('');
    setOriginalDefinition('');
    setIsEditing(false);
    clearTranslatedTerm();
    onClose();
  };

  const handleTranslate = async () => {
    if (!definition.trim()) {
      return;
    }

    await createTranslation(definition);
    setOriginalDefinition(definition);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDefinition('');
    setOriginalDefinition('');
    setIsEditing(false);
    clearTranslatedTerm();
    onClose();
  };

  const handleResetTranslation = () => {
    clearTranslatedTerm();
    setOriginalDefinition('');
    setIsEditing(false);
  };

  const handleEditStart = () => {
    setIsEditing(true);
  };

  const handleUpdateTerm = async () => {
    if (!definition.trim()) {
      return;
    }

    await createTranslation(definition);
    setOriginalDefinition(definition);
    setIsEditing(false);
  };

  const handleReset = () => {
    setDefinition(originalDefinition);
    setIsEditing(false);
  };

  const isInputDisabled = translatedTerm && !isEditing;

  return (
    <FormContainer isOpen={isOpen}>
      <FormHeader>
        <H3>Add new Business Logic</H3>
        <NewButton tertiary onClick={handleCancel}>
          <Icon name='close' color='var(--content-subtitle)' displaySize={20} />
        </NewButton>
      </FormHeader>
      <StyledForm onSubmit={handleFormSubmit} noValidate>
        <FormBody>
          <DefinitionLabelContainer>
            <H4>Definition</H4>
            <Tooltip
              content="Describe the logic you'd like Supper to understand and apply"
              position='top'
            >
              <Icon name='information' color='var(--content-subtitle)' displaySize={16} />
            </Tooltip>
            {translatedTerm && !isEditing && (
              <EditTranslateContainer>
                <NewButton onClick={handleEditStart}>
                  <Icon name='edit' color='var(--content-subtitle)' displaySize={16} />
                  Edit
                </NewButton>
              </EditTranslateContainer>
            )}
            {translatedTerm && isEditing && (
              <EditTranslateContainer>
                <NewButton secondary onClick={handleReset}>
                  Reset
                </NewButton>
                <NewButton
                  primary
                  onClick={handleUpdateTerm}
                  disabled={!definition.trim() || isTranslating}
                >
                  Update term
                </NewButton>
              </EditTranslateContainer>
            )}
          </DefinitionLabelContainer>
          <StyledInputBox>
            <Input
              as='textarea'
              placeholder='e.g. Lifetime value is the total amount spent over the lifetime of a customer'
              value={definition}
              onChange={(e) => setDefinition(e.target.value)}
              rows={3}
              disabled={isInputDisabled}
            />
          </StyledInputBox>
          <BusinessLogicLoader isLoading={isTranslating} translatedTerm={translatedTerm} />
        </FormBody>
        <FormFooter>
          {!translatedTerm ? (
            <NewButton
              primary
              onClick={handleTranslate}
              disabled={!definition.trim() || isTranslating}
            >
              Translate logic
            </NewButton>
          ) : (
            <>
              <NewButton secondary onClick={handleResetTranslation}>
                Cancel
              </NewButton>
              <NewButton primary type='submit' disabled={!definition.trim() || isTranslating}>
                Create
              </NewButton>
            </>
          )}
        </FormFooter>
      </StyledForm>
    </FormContainer>
  );
};

AddNewBusinessLogicForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export { AddNewBusinessLogicForm };
